import { theme } from 'antd';
import { ExclamationCircleOutlined, LockOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { useAuth } from '../../Contexts/AuthContext';
import { TextService } from '../../Services/TextService';
import { SubscriptionStatus } from '../../Types/SubscriptionStatus';
import { CompanyRole } from '../../Types/CompanyRole';
import { AccountType } from '../../Types/AccountType';
import { formatToShortFormat } from '../../Helpers/DateHelpers';
import { getCurrentLanguage } from '../../Helpers/RouteHelper';

/**
 * One-line strip under the top menu, shown on every admin page when the company's paid
 * subscription is about to run out, has run out (grace period) or is locked.
 */
const SubscriptionBanner = () => {
    const { user } = useAuth();
    const { token } = theme.useToken();
    const GetText = TextService.GetText;

    const status = user?.subscriptionStatus;
    if (!user || status === undefined || status === SubscriptionStatus.Active) {
        return null;
    }

    const renewalDate = formatToShortFormat(user.nextSubscriptionRenewalDate);
    const lockDate = formatToShortFormat(user.subscriptionLockDate);

    // A trial gets its own wording ("testperiod", "teckna") but the same steps and lock.
    const isTrial = user.accountType === AccountType.Trial;
    const prefix = isTrial ? "Subscription_Trial" : "Subscription_";
    const text = status === SubscriptionStatus.ExpiringSoon ? GetText(prefix + "ExpiringSoon").replace("{0}", renewalDate)
        : status === SubscriptionStatus.Expired ? GetText(prefix + "Expired").replace("{0}", renewalDate).replace("{1}", lockDate)
        : GetText(prefix + "Locked").replace("{0}", renewalDate);

    const isWarning = status === SubscriptionStatus.ExpiringSoon;
    // antd's colorWarningText/colorErrorText are too light on the tinted background, so the
    // text uses the normal text color and only the icon carries the warning/error color.
    const iconColor = isWarning ? token.colorWarning : token.colorError;
    const color = token.colorText;

    // Only the account owner can pay; other admins are told to contact them.
    const canRenew = user.role === CompanyRole.GlobalAdmin;

    return <div
        role="status"
        data-testid="subscription-banner"
        style={{
            background: isWarning ? token.colorWarningBg : token.colorErrorBg,
            borderBottom: `1px solid ${isWarning ? token.colorWarningBorder : token.colorErrorBorder}`,
            color,
            fontSize: 13,
            padding: "6px 16px",
            // The pages start right at the top, so the banner keeps its own distance to them.
            marginBottom: 16,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
            flexWrap: "wrap",
            textAlign: "center",
        }}
    >
        {status === SubscriptionStatus.Locked ? <LockOutlined style={{ color: iconColor }} /> : <ExclamationCircleOutlined style={{ color: iconColor }} />}
        <span>{text}</span>
        {canRenew ?
            <Link to={"/" + getCurrentLanguage() + "/payments"} style={{ color, fontWeight: 600, textDecoration: "underline" }}>
                {GetText(isTrial ? "Subscription_TrialSubscribeLink" : "Subscription_RenewLink")}
            </Link>
            : <span>{GetText("Subscription_ContactAccountOwner")}</span>}
    </div>;
};

export default SubscriptionBanner;
